import {create} from 'zustand';
import {AdventureSummaryDTO} from '@hexcrawl/shared-utils';
import {adventureService} from '../services/adventureService';

/** A placeholder slot for adventures not yet released. */
interface ComingSoonSlot {
  id: string;           // e.g. "coming-soon-0" — never passed as adventureId
  isComingSoon: true;
  title: 'Coming Soon';
  levels: { min: number; max: number };
}

/** AdventureSummaryDTO with discriminant for union narrowing. */
interface RealAdventureSlot extends AdventureSummaryDTO {
  isComingSoon: false;
}

export type AdventureSlot = RealAdventureSlot | ComingSoonSlot;

interface AdventureStore {
  /** Raw adventures returned by the server (1–3). */
  adventures: AdventureSummaryDTO[];
  /** Exactly 3 slots, padded with Coming Soon entries. */
  slots: AdventureSlot[];
  selectedAdventureId: string | null;
  isLoading: boolean;
  error: string | null;
  fetchAdventures: (level: number) => Promise<void>;
  selectAdventure: (id: string | null) => void;
}

const PAD_SIZE = 3;

function padToSlots(advantages: AdventureSummaryDTO[]): AdventureSlot[] {
  const slots: AdventureSlot[] = advantages.map((a) => ({ ...a, isComingSoon: false }));
  while (slots.length < PAD_SIZE) {
    slots.push({ id: `coming-soon-${slots.length}`, isComingSoon: true, title: 'Coming Soon', levels: { min: 1, max: 99 } });
  }
  return slots;
}

export const useAdventureStore = create<AdventureStore>((set) => ({
  adventures: [],
  slots: [],
  selectedAdventureId: null,
  isLoading: false,
  error: null,

  fetchAdventures: async (level: number) => {
    set({isLoading: true, error: null});
    try {
      const adventures = await adventureService.getAdventures(level);
      set({adventures, slots: padToSlots(adventures), isLoading: false});
    } catch (e: any) {
      set({error: e?.message || 'Failed to load adventures', isLoading: false});
    }
  },

  selectAdventure: (id) => set({selectedAdventureId: id}),
}));
